/**
 * Page publique de confirmation de créneau (lien reçu par email).
 * Le client voit les créneaux proposés et en choisit un — sans authentification.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { CalendarCheck } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { BrandLogo } from '@/components/site/brand';
import { extractErrorMessage } from '@/features/auth/error-message';
import { formatSlot } from '@/features/appointments/appointment-labels';
import {
  confirmSlot,
  getAppointmentByToken,
} from '@/features/appointments/appointments-api';

export function AppointmentConfirmPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const data = useQuery({
    queryKey: ['appointment-token', token],
    queryFn: () => getAppointmentByToken(token),
    enabled: Boolean(token),
    retry: false,
  });

  const confirmMut = useMutation({
    mutationFn: (slotIndex: number) => confirmSlot(token, slotIndex),
    onSuccess: () => toast.success('Créneau confirmé, merci !'),
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const confirmed =
    confirmMut.isSuccess || data.data?.status === 'confirme';

  return (
    <div className="grid min-h-screen place-items-center bg-bcl-grey p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2">
            <BrandLogo size="sm" />
          </div>
          <CardTitle className="flex items-center justify-center gap-2">
            <CalendarCheck className="h-5 w-5 text-bcl-blue" />
            Choisissez votre créneau
          </CardTitle>
          <CardDescription>
            {data.data?.projectReference
              ? `Dossier ${data.data.projectReference}`
              : 'Rendez-vous BCL Telecom'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!token || data.isError ? (
            <p className="text-center text-sm text-red-600">
              Lien de confirmation invalide ou expiré.
            </p>
          ) : confirmed ? (
            <p className="text-center text-sm text-green-700">
              Votre créneau est confirmé. BCL vous recontactera.
            </p>
          ) : data.isLoading ? (
            <p className="text-center text-sm text-gray-500">Chargement…</p>
          ) : (
            (data.data?.proposedSlots ?? []).map((slot, index) => (
              <Button
                key={slot}
                variant="outline"
                className="w-full justify-between"
                disabled={confirmMut.isPending}
                onClick={() => confirmMut.mutate(index)}
              >
                {formatSlot(slot)}
                <span className="text-bcl-blue">Choisir</span>
              </Button>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
